import React, { useCallback, useEffect, useState } from 'react';
import { RefreshCw, Sparkles, ExternalLink, AlertTriangle } from 'lucide-react';
import { getAIUsage } from '../api';

// Settings → AI Usage & Cost.
//
// What the AI drafts coming from the phones are costing: per rep per week,
// totals for the range, each rep's count against today's cap, and the last
// few calls. Everything comes from the server's call log, which records the
// shape of each call (who, when, tokens, time, outcome) and never the note.
// Dollar figures are estimates — token counts times the prices set in the
// server's .env; the invoice itself is in the Anthropic console.

const WEEK_OPTIONS = [4, 8, 13, 26, 52];

const num = (n) => (n == null ? '—' : Number(n).toLocaleString());
const money = (v) => (v == null ? '—' : `$${Number(v).toFixed(2)}`);
const secs = (ms) => (ms == null ? '—' : `${(ms / 1000).toFixed(1)}s`);
const when = (iso) => {
  if (!iso) return '—';
  const d = new Date(iso.includes('T') ? iso : iso.replace(' ', 'T') + 'Z');
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleString();
};
const weekLabel = (start) => {
  if (!start) return '—';
  const d = new Date(start + 'T00:00:00');
  return Number.isNaN(d.getTime()) ? start : `Week of ${d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}`;
};

// The company's monthly AI allowance, set by the platform owner.
export function monthlyText(monthly) {
  if (!monthly) return '—';
  const used = Number(monthly.used || 0).toLocaleString();
  if (monthly.limit == null) return `${used} tokens, no monthly cap`;
  const resets = monthly.resets_at ? new Date(monthly.resets_at) : null;
  const on = resets && !Number.isNaN(resets.getTime())
    ? resets.toLocaleDateString(undefined, { month: 'short', day: 'numeric', timeZone: 'UTC' })
    : 'the 1st';
  const full = Number(monthly.used || 0) >= Number(monthly.limit);
  return `${used} of ${Number(monthly.limit).toLocaleString()} tokens${full ? ', used up' : ''} (starts over ${on})`;
}

function Tile({ label, value, hint }) {
  return (
    <div style={{ padding: '14px 16px', borderRadius: '10px', background: 'var(--bg-secondary, rgba(127,127,127,0.08))', minWidth: 0 }}>
      <div style={{ fontSize: '12px', color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.04em' }}>{label}</div>
      <div style={{ fontSize: '22px', fontWeight: 700, color: 'var(--text-primary)', marginTop: '4px', overflowWrap: 'anywhere' }}>{value}</div>
      {hint && <div style={{ fontSize: '12px', color: 'var(--text-muted, var(--text-secondary))', marginTop: '2px' }}>{hint}</div>}
    </div>
  );
}

export default function AIUsagePanel() {
  const [weeks, setWeeks] = useState(8);
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const { data } = await getAIUsage(weeks);
      setReport(data);
    } catch (err) {
      setError(err.response?.data?.error || 'Could not load AI usage.');
    } finally {
      setLoading(false);
    }
  }, [weeks]);

  useEffect(() => { load(); }, [load]);

  const totals = report?.totals || {};
  const rows = report?.rows || [];
  const pricesConfigured = Boolean(report?.config?.prices_configured);

  // Rows arrive per week per rep, most recent week first; group them for display.
  const byWeek = [];
  for (const row of rows) {
    const last = byWeek[byWeek.length - 1];
    if (last && last.week_start === row.week_start) last.rows.push(row);
    else byWeek.push({ week_start: row.week_start, rows: [row] });
  }

  return (
    <div className="card">
      <div className="card-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '16px', flexWrap: 'wrap' }}>
        <div>
          <h3 className="card-title" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}><Sparkles size={18} /> AI Usage &amp; Cost</h3>
          <p style={{ fontSize: '13px', color: 'var(--text-secondary)', margin: '4px 0 0' }}>
            Drafts the phones asked the AI for, per rep per week. Counts and tokens come from the server's call log; it records who, when, how big and how long — never the note itself.
          </p>
        </div>
        <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexShrink: 0 }}>
          <label htmlFor="ai-usage-weeks" className="sr-only">Range</label>
          <select id="ai-usage-weeks" className="form-select" value={weeks} onChange={(e) => setWeeks(Number(e.target.value))} style={{ width: 'auto' }}>
            {WEEK_OPTIONS.map((w) => <option key={w} value={w}>Last {w} weeks</option>)}
          </select>
          <button type="button" className="btn btn-secondary" onClick={load} disabled={loading} title="Reload">
            <RefreshCw size={16} /> Refresh
          </button>
        </div>
      </div>

      <div className="card-body">
        {error && <div className="alert alert-error" role="alert">{error}</div>}

        {loading && !report ? (
          <div className="loading-container"><div className="spinner"></div></div>
        ) : report && (
          <>
            <div className="grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '12px', marginBottom: '20px' }}>
              <Tile label="Drafts" value={num(totals.calls)} hint={`${num(totals.ok)} succeeded`} />
              <Tile label="Failed" value={num(totals.failed)} hint="refused, rate-limited or errored" />
              <Tile label="Tokens in" value={num(totals.input_tokens)} hint={`${num(totals.cache_read_input_tokens)} from cache`} />
              <Tile label="Tokens out" value={num(totals.output_tokens)} />
              <Tile label="Estimated cost" value={pricesConfigured ? money(totals.cost_usd) : '—'} hint={pricesConfigured ? 'tokens × configured prices' : 'set prices to see dollars'} />
              <Tile label="Avg draft time" value={secs(totals.avg_duration_ms)} />
            </div>

            {!pricesConfigured && (
              <div className="alert" role="note" style={{ display: 'flex', gap: '10px', alignItems: 'flex-start', padding: '12px 14px', borderRadius: '10px', background: 'rgba(245, 158, 11, 0.10)', color: 'var(--text-primary)', marginBottom: '20px' }}>
                <AlertTriangle size={18} style={{ flexShrink: 0, marginTop: '1px', color: '#d97706' }} />
                <div style={{ fontSize: '13px' }}>
                  <strong>No prices configured, so no dollar figures.</strong> Add <code>AI_COST_INPUT_USD_PER_MTOK</code> and <code>AI_COST_OUTPUT_USD_PER_MTOK</code> (US dollars per million tokens for <code>{report.config?.model || 'the model'}</code>) to the server's <code>.env</code> and restart. Token counts are exact either way; the figures here will be an estimate — the invoice is in the Anthropic console.
                </div>
              </div>
            )}

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: '16px', marginBottom: '20px' }}>
              <div>
                <h4 style={{ fontSize: '13px', fontWeight: 600, color: 'var(--text-secondary)', margin: '0 0 8px', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Today, against the daily cap</h4>
                {report.today?.length ? (
                  <ul style={{ listStyle: 'none', margin: 0, padding: 0, fontSize: '14px' }}>
                    {report.today.map((t) => (
                      <li key={t.user_id ?? 'none'} style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0', borderBottom: '1px solid var(--border-color, rgba(127,127,127,0.2))' }}>
                        <span>{t.user_name || `User ${t.user_id}`}</span>
                        <span style={{ color: t.calls >= t.cap ? '#dc2626' : 'var(--text-secondary)' }}>{num(t.calls)} / {num(t.cap)}</span>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p style={{ fontSize: '14px', color: 'var(--text-secondary)', margin: 0 }}>No drafts yet today.</p>
                )}
              </div>
              <div>
                <h4 style={{ fontSize: '13px', fontWeight: 600, color: 'var(--text-secondary)', margin: '0 0 8px', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Configuration</h4>
                <dl style={{ display: 'grid', gridTemplateColumns: 'auto 1fr', gap: '6px 16px', fontSize: '14px', margin: 0 }}>
                  <dt style={{ color: 'var(--text-secondary)' }}>Model</dt><dd style={{ margin: 0 }}>{report.config?.model || '—'}</dd>
                  <dt style={{ color: 'var(--text-secondary)' }}>Per person per day</dt><dd style={{ margin: 0 }}>{num(report.config?.daily_cap)} AI calls, counting every AI feature (the assistant, capture and reviews)</dd>
                  <dt style={{ color: 'var(--text-secondary)' }}>Company this month</dt>
                  <dd style={{ margin: 0 }} data-testid="monthly-allowance">{monthlyText(report.monthly)}</dd>
                  <dt style={{ color: 'var(--text-secondary)' }}>Prices</dt>
                  <dd style={{ margin: 0 }}>
                    {pricesConfigured
                      ? `$${report.prices.input_usd_per_mtok}/M in · $${report.prices.output_usd_per_mtok}/M out · $${report.prices.cache_read_usd_per_mtok}/M cached`
                      : 'not set'}
                  </dd>
                  <dt style={{ color: 'var(--text-secondary)' }}>Invoice</dt>
                  <dd style={{ margin: 0 }}>
                    <a href="https://console.anthropic.com/settings/billing" target="_blank" rel="noopener noreferrer" style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                      Anthropic console <ExternalLink size={14} />
                    </a>
                  </dd>
                </dl>
              </div>
            </div>
          </>
        )}
      </div>

      {report && (
        <>
          <div className="table-container">
            <table className="table" aria-label="AI usage per rep per week">
              <thead>
                <tr>
                  <th scope="col">Week</th>
                  <th scope="col">Rep</th>
                  <th scope="col" style={{ textAlign: 'right' }}>Drafts</th>
                  <th scope="col" style={{ textAlign: 'right' }}>Failed</th>
                  <th scope="col" style={{ textAlign: 'right' }}>Tokens in</th>
                  <th scope="col" style={{ textAlign: 'right' }}>Tokens out</th>
                  <th scope="col" style={{ textAlign: 'right' }}>Avg time</th>
                  <th scope="col" style={{ textAlign: 'right' }}>Est. cost</th>
                </tr>
              </thead>
              <tbody>
                {byWeek.length === 0 ? (
                  <tr><td colSpan="8"><div className="empty-state"><Sparkles size={48} className="empty-state-icon" /><h3 className="empty-state-title">No AI drafts in this range</h3><p>When a rep taps Review on a capture, the call shows up here.</p></div></td></tr>
                ) : byWeek.map((week) => week.rows.map((r, i) => (
                  <tr key={`${week.week_start}-${r.user_id}`}>
                    <td style={{ whiteSpace: 'nowrap', color: i === 0 ? 'var(--text-primary)' : 'transparent' }}>{weekLabel(week.week_start)}</td>
                    <td>{r.user_name || `User ${r.user_id}`}</td>
                    <td style={{ textAlign: 'right' }}>{num(r.calls)}</td>
                    <td style={{ textAlign: 'right', color: r.failed ? '#dc2626' : undefined }}>{num(r.failed)}</td>
                    <td style={{ textAlign: 'right' }}>{num(r.input_tokens)}</td>
                    <td style={{ textAlign: 'right' }}>{num(r.output_tokens)}</td>
                    <td style={{ textAlign: 'right' }}>{secs(r.avg_duration_ms)}</td>
                    <td style={{ textAlign: 'right' }}>{pricesConfigured ? money(r.cost_usd) : '—'}</td>
                  </tr>
                )))}
              </tbody>
            </table>
          </div>

          {report.proposals?.rows?.length > 0 && (
            <div className="card" style={{ marginTop: 16 }}>
              <div className="card-header">
                <h4 style={{ fontSize: '13px', fontWeight: 600, color: 'var(--text-secondary)', margin: '8px 0', textTransform: 'uppercase', letterSpacing: '0.04em' }}>What the AI proposed, and what people did with it</h4>
              </div>
              <div className="table-container">
                <table className="table" aria-label="AI proposals and outcomes">
                  <thead>
                    <tr><th>From</th><th>Kind</th><th style={{ textAlign: 'right' }}>Proposed</th><th style={{ textAlign: 'right' }}>Accepted</th><th style={{ textAlign: 'right' }}>Edited</th><th style={{ textAlign: 'right' }}>Rejected</th><th style={{ textAlign: 'right' }}>Undecided</th><th style={{ textAlign: 'right' }}>Taken</th></tr>
                  </thead>
                  <tbody>
                    {report.proposals.rows.map((r) => (
                      <tr key={`${r.source}-${r.kind}`}>
                        <td>{r.source === 'capture' ? 'The note' : 'The assistant'}</td>
                        <td>{r.kind}</td>
                        <td style={{ textAlign: 'right' }}>{num(r.proposed)}</td>
                        <td style={{ textAlign: 'right' }}>{num(r.accepted)}</td>
                        <td style={{ textAlign: 'right' }}>{num(r.edited)}</td>
                        <td style={{ textAlign: 'right' }}>{num(r.rejected)}</td>
                        <td style={{ textAlign: 'right' }}>{num(r.pending)}</td>
                        <td style={{ textAlign: 'right' }}>{r.acceptance_pct == null ? '—' : `${r.acceptance_pct}%`}</td>
                      </tr>
                    ))}
                    {report.proposals.totals && (
                      <tr style={{ fontWeight: 600 }}>
                        <td colSpan={2}>All</td>
                        <td style={{ textAlign: 'right' }}>{num(report.proposals.totals.proposed)}</td>
                        <td style={{ textAlign: 'right' }}>{num(report.proposals.totals.accepted)}</td>
                        <td style={{ textAlign: 'right' }}>{num(report.proposals.totals.edited)}</td>
                        <td style={{ textAlign: 'right' }}>{num(report.proposals.totals.rejected)}</td>
                        <td style={{ textAlign: 'right' }}>{num(report.proposals.totals.pending)}</td>
                        <td style={{ textAlign: 'right' }}>{report.proposals.totals.acceptance_pct == null ? '—' : `${report.proposals.totals.acceptance_pct}%`}</td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
              <p className="text-muted text-sm" style={{ padding: '8px 16px' }}>Taken = accepted or edited, of those decided. The honest measure of whether the AI is useful; a kind that is mostly rejected needs its prompt looked at.</p>
            </div>
          )}

          {report.recent?.length > 0 && (
            <>
              <div className="card-body" style={{ paddingBottom: 0 }}>
                <h4 style={{ fontSize: '13px', fontWeight: 600, color: 'var(--text-secondary)', margin: '8px 0', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Last {report.recent.length} calls</h4>
              </div>
              <div className="table-container">
                <table className="table" aria-label="Recent AI calls">
                  <thead>
                    <tr>
                      <th scope="col">When</th>
                      <th scope="col">Rep</th>
                      <th scope="col">Result</th>
                      <th scope="col">Input</th>
                      <th scope="col" style={{ textAlign: 'right' }}>Tokens</th>
                      <th scope="col" style={{ textAlign: 'right' }}>Time</th>
                      <th scope="col" style={{ textAlign: 'right' }}>Est. cost</th>
                    </tr>
                  </thead>
                  <tbody>
                    {report.recent.map((c) => (
                      <tr key={c.id}>
                        <td style={{ whiteSpace: 'nowrap' }}>{when(c.created_at)}</td>
                        <td>{c.user_name || `User ${c.user_id}`}</td>
                        <td>
                          {c.status === 'ok'
                            ? <span className="badge badge-success">OK</span>
                            : <span className="badge badge-danger" title={c.error || ''}>{c.status === 'capped' ? 'Capped' : `Failed${c.http_status ? ` (${c.http_status})` : ''}`}</span>}
                        </td>
                        <td style={{ color: 'var(--text-secondary)' }}>
                          {[c.text_chars != null ? `${num(c.text_chars)} chars` : null, c.image_count ? `${c.image_count} photo${c.image_count === 1 ? '' : 's'}` : null, c.is_revision ? 'revision' : null].filter(Boolean).join(' · ') || '—'}
                        </td>
                        <td style={{ textAlign: 'right' }}>{c.input_tokens == null ? '—' : `${num(c.input_tokens)} / ${num(c.output_tokens)}`}</td>
                        <td style={{ textAlign: 'right' }}>{secs(c.duration_ms)}</td>
                        <td style={{ textAlign: 'right' }}>{pricesConfigured ? money(c.cost_usd) : '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </>
      )}
    </div>
  );
}
